/**
 * Notification body helpers (attachments + system messages + text).
 *
 * RU strings mirror the BE FCM/APN body table (revolt_backend) verbatim,
 * so desktop and push notifications show the same text.
 *
 * Parameter types below are structural, not the API schema types.
 */

import removeMd from "remove-markdown";

/**
 * Fallback notification body for a message without text:
 * label of the first attachment. No Gif variant exists in the API.
 */
export function getAttachmentFallback(
    attachments?: { metadata: { type: string } }[] | null,
): string {
    const type = attachments?.[0]?.metadata.type;

    switch (type) {
        case "Image":
            return "Фото";
        case "Video":
            return "Видео";
        case "Audio":
            return "Аудио";
        case "Text":
        case "File":
            return "Документ";
        default:
            return "Пустое сообщение";
    }
}

/**
 * Chat-list preview for a message without text (redmine #99371):
 * icon + label of the first attachment, or a count for several.
 * Labels mirror `getAttachmentFallback` (BE consistency).
 * undefined/null/[] → null.
 */
export function getAttachmentPreview(
    attachments?: { metadata: { type: string } }[] | null,
): {
    kind: "single" | "multiple";
    icon: "photo" | "video" | "audio" | "file";
    label: string; // single: "Фото"|"Видео"|"Аудио"|"Документ"; multiple: "" (i18n in the component)
    count: number;
} | null {
    if (!attachments || attachments.length === 0) return null;

    if (attachments.length > 1) {
        return {
            kind: "multiple",
            icon: "file",
            label: "",
            count: attachments.length,
        };
    }

    switch (attachments[0]?.metadata.type) {
        case "Image":
            return { kind: "single", icon: "photo", label: "Фото", count: 1 };
        case "Video":
            return { kind: "single", icon: "video", label: "Видео", count: 1 };
        case "Audio":
            return { kind: "single", icon: "audio", label: "Аудио", count: 1 };
        // Text, File and unknown types — like getAttachmentFallback's file label.
        default:
            return {
                kind: "single",
                icon: "file",
                label: "Документ",
                count: 1,
            };
    }
}

/**
 * Split text into text/emoji segments by the emoji shortcode regex
 * (same as RE_EMOJI; re-declared here to keep this file import-free).
 * Text segments are exact substrings (spaces included); empty text
 * pieces are dropped (":a::b:" → two emoji segments only).
 */
export function segmentEmojis(text: string): {
    type: "text" | "emoji";
    value: string;
}[] {
    const segments: { type: "text" | "emoji"; value: string }[] = [];
    const re = /:([a-zA-Z0-9\-_]+):/g;
    let last = 0;

    for (const match of text.matchAll(re)) {
        const index = match.index ?? 0;
        const textPart = text.slice(last, index);
        if (textPart) segments.push({ type: "text", value: textPart });
        segments.push({ type: "emoji", value: match[1] });
        last = index + match[0].length;
    }

    const rest = text.slice(last);
    if (rest) segments.push({ type: "text", value: rest });

    return segments;
}

/**
 * Notification body for a system (non-text) message.
 * `name` is the resolved display name for `system.id` (user_* arms);
 * when missing, the raw id is used (BE parity). Unknown type → `""`,
 * the store falls back to its default body.
 */
export function getSystemMessageBody(
    system: { type: string; id?: string; name?: string; content?: string },
    name?: string,
): string {
    switch (system.type) {
        case "user_added":
            return `${name ?? system.id} добавлен в канал`;
        case "user_remove":
            return `${name ?? system.id} удалён из канала`;
        case "user_joined":
            return `${name ?? system.id} присоединился`;
        case "user_left":
            return `${name ?? system.id} покинул`;
        case "user_kicked":
            return `${name ?? system.id} исключён`;
        case "user_banned":
            return `${name ?? system.id} заблокирован`;
        case "channel_renamed":
            return `Канал переименован в ${system.name}`;
        case "channel_description_changed":
            return "Описание канала изменено";
        case "channel_icon_changed":
            return "Иконка канала изменена";
        case "channel_ownership_changed":
            return "Собственник канала изменён";
        case "message_pinned":
            return "Сообщение закреплено";
        case "message_unpinned":
            return "Сообщение откреплено";
        case "text":
            return system.content ?? "";
        default:
            return "";
    }
}

// Mention shapes, kept in sync with revolt.js (packages/revolt.js/src/Client.ts
// RE_MENTIONS / RE_MENTION_ROLES / RE_SPOILER): re-declared here so the
// pipeline below stays importable under plain node.
const RE_MENTIONS = /<@([A-z0-9]{26})>/g;
const RE_MENTION_ROLES = /<%([A-z0-9]{26})>/g;
const RE_SPOILER = /!!.+?!!/g;

/**
 * Pure pipeline for the push-notification body of a text message:
 * mass mentions → user mentions → role mentions → spoilers → markdown strip.
 * Tested by test/notification-body.ts.
 *
 * `<@everyone>` / `<@online>` must be resolved before remove-md: it treats
 * them as HTML tags and drops them, which left an empty body for
 * mention-only messages (#104189). The result is trimmed so a whitespace-only
 * body becomes "" and the attachment fallback (NotificationOptions) applies.
 *
 * `resolveUserMention` / `resolveRoleMention` map mention ids to display
 * names; unresolved tokens are kept (remove-md drops them, same as the BE
 * dropping unknown mention ids).
 */
export function prepareNotificationBody(
    text: string,
    resolveUserMention?: (id: string) => string | undefined,
    resolveRoleMention?: (id: string) => string | undefined,
): string {
    text = text
        .replace(/<@everyone>/g, "@everyone")
        .replace(/<@online>/g, "@online");

    if (resolveUserMention) {
        text = text.replace(RE_MENTIONS, (sub: string, id: string) => {
            return resolveUserMention(id) ?? sub;
        });
    }

    if (resolveRoleMention) {
        text = text.replace(RE_MENTION_ROLES, (sub: string, id: string) => {
            return resolveRoleMention(id) ?? sub;
        });
    }

    text = text.replace(RE_SPOILER, (match: string) => {
        return "█".repeat(match.length);
    });

    return removeMd(text).trim();
}
